export type Coordinates = {
  lat: number
  lng: number 
}

export type DistanceResult = {
  meters: number
  kilometers: number
  minutes: number
}

/**
 * Calculates distance between 2 geo points + estimated time.
 * @param from Starting location
 * @param to Destination
 * @param speedKmH Average speed in km/h (default: 40 km/h for driving)
 */
export function calculateDistanceAndTime(
  from: Coordinates,
  to: Coordinates,
  branch : string,
  speedKmH: number = 40,
): DistanceResult {
  const fLat = Number(from.lat);
  const fLng = Number(from.lng);
  const tLat = Number(to.lat);
  const tLng = Number(to.lng);

  // Return default if coordinates are missing or invalid
  if (
    isNaN(fLat) || isNaN(fLng) || isNaN(tLat) || isNaN(tLng) ||
    (fLat === 0 && fLng === 0) || (tLat === 0 && tLng === 0)
  ) {
    return { meters: 0, kilometers: 0, minutes: 0 };
  }


  const R = 6371_000; // Earth radius in meters

  const dLat = toRad((tLat ) - (fLat));
  const dLng = toRad((tLng) - (fLng));

  const lat1 = toRad(fLat);
  const lat2 = toRad(tLat);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const meters = R * c;
  const kilometers = meters / 1000;

  // Time in hours: distance / speed
  const hours = kilometers / speedKmH;
  const minutes = hours * 60;

  return {
    meters: Math.round(meters),
    kilometers: Number(kilometers.toFixed(2)),
    minutes: Math.ceil(minutes),
  };
}

function toRad(value: number) {
  return (value * Math.PI) / 180;
}

/**
 * Calculates actual road distance and duration using OSRM API.
 * Note: This is asynchronous and requires an internet connection.
 */
export async function calculateRoadDistance(
  from: Coordinates,
  to: Coordinates
): Promise<DistanceResult | null> {
  // Ensure inputs are valid numbers
  const fLat = Number(from.lat);
  const fLng = Number(from.lng);
  const tLat = Number(to.lat);
  const tLng = Number(to.lng);

  // Return null if coordinates are missing, invalid, or zero (0,0 is usually invalid for this context)
  if (
    isNaN(fLat) || isNaN(fLng) || isNaN(tLat) || isNaN(tLng) ||
    (fLat === 0 && fLng === 0) || (tLat === 0 && tLng === 0)
  ) {
    return null;
  }

  try {
    // OSRM expects coordinates in "longitude,latitude" format
    // Using toFixed(6) ensures standard formatting and reasonable precision
    const url = `https://router.project-osrm.org/route/v1/driving/${fLng.toFixed(6)},${fLat.toFixed(6)};${tLng.toFixed(6)},${tLat.toFixed(6)}?overview=false`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();

    if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
      const route = data.routes[0];
      // OSRM returns distance in meters and duration in seconds
      const meters = route.distance;
      const seconds = route.duration;

      return {
        meters: Math.round(meters),
        kilometers: Number((meters / 1000).toFixed(2)),
        minutes: Math.ceil(seconds / 60),
      };
    }
    return null;
  } catch (error) {
    return null;
  }
}
